import { memo, useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { useSessionStorage } from '@mantine/hooks';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  FetchedFormattedInputChoices,
  SelectDropdownInput,
  FetchFnParams,
} from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { TextDropdownInput } from 'components/Inputs/Dropdown/TextDropdownInput';
import {
  useGetFetchAndFormatAuthors,
  useGetFetchAndFormatKeywords,
  useGetFetchAndFormatSections,
} from 'components/Search/components/SearchAdvance/components/hooks';
import {
  BloxCustomDataProviderType,
  UserAccountSummary,
} from 'utility/types/dataProvider';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  checkChangePublishStatusPermission,
  checkChangeWorkflowPermission,
  checkSiteAdminPermission,
} from 'utility/permissions';
import { InputChoice } from 'components/Inputs/Dropdown/dropdownConsts';
import { useDataProvider } from 'react-admin';
import { useNxtImport } from '../NxtImportContext';
import {
  getAuthorPrimaryText,
  getAuthorSecondaryText,
  getAuthorAccountTypeChip,
} from 'components/FormControls/ListSearchSelect/helpers/authorDisplayHelpers';

export type TBatchImportUploadPresetsForm = {
  workflow_id: string;
  workflow_process_id: string;
  publish_status: string;
  slug: string;
  authors: UserAccountSummary['uuid'][];
  keywords: string[];
  sections: string[];
  is_publish_status_disabled_by_workflow: boolean;
};

// Storage type for workflow options.
type WorkflowOptionsStorage = {
  loading: boolean;
  options: FetchedFormattedInputChoices | undefined;
};

const getSelectedWorkflow = ({
  currentWorkflowId,
  workflowOptions,
}: {
  currentWorkflowId: string;
  workflowOptions: WorkflowOptionsStorage | undefined;
}) => {
  return workflowOptions?.options?.items?.find(
    (option: InputChoice) => option.value === currentWorkflowId,
  );
};

export const BatchImportUploadPresets = memo(() => {
  const { t } = useTranslation();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const { accordionExpanded, setAccordionExpanded } = useNxtImport();
  const { sessionConfig, userInfo } = useCosmos();

  // Use session storage to persist workflow options across page reloads
  const [workflowOptions, setWorkflowOptions] = useSessionStorage<
    WorkflowOptionsStorage | undefined
  >({
    key: 'batch-import-workflow-options',
    defaultValue: undefined,
  });

  // Fetch functions for dropdown data
  const { fetchAndFormatAuthors } = useGetFetchAndFormatAuthors();
  const { fetchAndFormatSections } = useGetFetchAndFormatSections();
  const { fetchAndFormatKeywords } = useGetFetchAndFormatKeywords();

  const fetchAndFormatBulkUploadWorkflows = async ({
    filterTextDebounced,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const result = await dataProvider.getWorkflows({
        query: filterTextDebounced,
      });
      if (!Array.isArray(result?.data)) {
        throw new Error('fetchAndFormatBatchEditWorkflows: Invalid data format');
      }

      const items = result.data.map((workflow) => {
        const isFirstProcessForceDraft = Boolean(workflow.processes?.[0]?.force_draft);

        return {
          value: `${workflow.id}`,
          label: workflow.name,
          __isHeader: true,
          isFirstProcessForceDraft,
        };
      });

      const workflowData: WorkflowOptionsStorage = {
        loading: false,
        options: {
          total: items.length,
          next: null,
          prev: null,
          items: items.map((item) => ({
            ...item,
            label: item.label ?? 'Untitled', // or any default value
          })),
        },
      };

      // Store in session storage: needed for disabling publish status based on workflow selection.
      setWorkflowOptions(workflowData);

      return {
        total: items.length,
        next: null,
        prev: null,
        items: items.map((item) => ({
          ...item,
          label: item.label ?? 'Untitled', // or any default value
        })),
      };
    } catch (error) {
      console.error('Error fetching workflows for batch import:', error);
      return {
        total: 0,
        next: null,
        prev: null,
        items: [],
      };
    }
  };

  // Use form context instead of getting form from BatchImport context
  const { control, watch, setValue } = useFormContext<TBatchImportUploadPresetsForm>();

  // Permissions
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userCanChangeWorkflow, name: workflowPermName } =
    checkChangeWorkflowPermission(sessionConfig);
  const { granted: userCanChangePublishStatus, name: publishStatusPermName } =
    checkChangePublishStatusPermission(sessionConfig);

  const canChangeWorkflow = Boolean(userIsAdmin || userCanChangeWorkflow);
  const canChangePublishStatus = Boolean(userIsAdmin || userCanChangePublishStatus);

  const watchedValues = watch();

  // Prepare static choices for current user to avoid unnecessary fetches
  const currentUserChoice: InputChoice | undefined = userInfo
    ? {
        label: getAuthorPrimaryText(userInfo),
        sublabel: getAuthorSecondaryText(userInfo) || undefined,
        value: userInfo.uuid || '',
        chip: getAuthorAccountTypeChip(userInfo),
        description: userInfo.email,
      }
    : undefined;

  // Update is_publish_status_disabled_by_workflow when workflow_id changes
  useEffect(() => {
    if (!workflowOptions) {
      return;
    }

    const selectedWorkflow = getSelectedWorkflow({
      currentWorkflowId: watchedValues.workflow_id,
      workflowOptions,
    });

    const isDisabledByWorkflow = Boolean(selectedWorkflow?.isFirstProcessForceDraft);
    setValue('is_publish_status_disabled_by_workflow', isDisabledByWorkflow);

    // Clear publish_status if workflow forces draft
    if (isDisabledByWorkflow) {
      setValue('publish_status', '');
    }
  }, [watchedValues.workflow_id, setValue, workflowOptions]);

  const filledFieldCount = useMemo(() => {
    const fieldsToCheck: (keyof TBatchImportUploadPresetsForm)[] = [
      'workflow_id',
      'authors',
      'publish_status',
      'keywords',
      'slug',
      'sections',
    ];

    return fieldsToCheck.reduce((count: number, fieldName) => {
      const value = watchedValues?.[fieldName];

      if (value === null || typeof value === 'undefined') {
        return count;
      }

      if (typeof value === 'string') {
        return value.trim() === '' ? count : count + 1;
      }

      if (typeof value === 'boolean') {
        return value ? count + 1 : count;
      }

      if (Array.isArray(value)) {
        return (value as []).length > 0 ? count + 1 : count;
      }

      return count + 1;
    }, 0);
  }, [watchedValues]);

  return (
    <Accordion
      data-testid="batch-import-upload-presets"
      expanded={accordionExpanded}
      onChange={(_, isExpanded) => setAccordionExpanded(isExpanded)}
      sx={(theme) => ({
        mb: 2,
        gap: 1.5,
        padding: 1,
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: `8px !important`,
        borderColor: theme.palette.grey[theme.palette.mode === 'light' ? 400 : 800],
        color: theme.palette.text.secondary,
        boxShadow: 'unset',
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="batch-import-upload-presets-panel"
        id="batch-import-upload-presets-summary"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {t('blox.batch_import.presets.label', 'Presets')}

        {/* Only show count when accordion is collapsed */}
        <Typography
          data-testid="batch-import-upload-presets-filled-count"
          sx={(theme) => {
            const bgColor = theme.blox.colors.red;
            return {
              backgroundColor: bgColor,
              borderRadius: '50%',
              p: 0.25,
              color: theme.blox.colors.white,
              fontSize: '0.75rem',
              width: '1.25rem',
              height: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              fontWeight: 'bold',
              lineHeight: 1,
              ml: 1,
              '.Mui-expanded ~ * &': {
                display: 'none', // Hide when expanded
              },
              '.MuiAccordionSummary-root:not(.Mui-expanded) &': {
                display: 'flex', // Show when collapsed
              },
            };
          }}
        >
          {filledFieldCount}
        </Typography>
      </AccordionSummary>

      {/* Preset Form. */}
      <AccordionDetails>
        <Box
          data-testid="batch-import-upload-presets-form"
          component="form"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {/* Workflow */}
          <Controller
            name="workflow_id"
            control={control}
            render={({ field }) => (
              <div>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5, fontSize: '14px' }}
                >
                  {t('blox.editorial.metadata.group.workflow.label')}
                </Typography>
                <Tooltip
                  title={
                    !canChangeWorkflow
                      ? t('blox.permissions.msg.required_short', {
                          name: workflowPermName,
                        })
                      : ''
                  }
                >
                  <span>
                    <SelectDropdownInput
                      data-testid="workflow-select-dropdown"
                      inputLabel={t('blox.editorial.metadata.group.workflow.label')}
                      value={field.value ?? ''}
                      updateValue={(_, value) => field.onChange(value)}
                      fetchDataFn={fetchAndFormatBulkUploadWorkflows}
                      showFilter={false}
                      showFooter={false}
                      showChipBox={true}
                      multiple={false}
                      popoverMatchesFormControlWidth
                      renderValueDisplay="chips"
                      formControlVariant="outlined"
                      inputPlaceholderText="stringify-options"
                      disabled={!canChangeWorkflow}
                    />
                  </span>
                </Tooltip>
              </div>
            )}
          />

          {/* Publish Status */}
          <Controller
            name="publish_status"
            control={control}
            render={({ field }) => {
              // Use the form's tracked value for workflow force_draft state
              const isDisabledByWorkflow =
                watchedValues.is_publish_status_disabled_by_workflow;

              // Field disabled state and tooltip message.
              const isDisabled = !canChangePublishStatus || isDisabledByWorkflow;
              const tooltipMessage = isDisabledByWorkflow
                ? t('blox.editorial.asset.properties.publish.help.force_draft_workflow')
                : !canChangePublishStatus
                  ? t('blox.permissions.msg.required_short', {
                      name: publishStatusPermName,
                    })
                  : '';

              return (
                <div>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5, fontSize: '14px' }}
                  >
                    {t('blox.editorial.asset.properties.no_publish.label_alt')}
                  </Typography>
                  <Tooltip
                    title={tooltipMessage}
                    placement="top"
                  >
                    <span>
                      <SelectDropdownInput
                        inputLabel={t(
                          'blox.editorial.asset.properties.no_publish.label_alt',
                        )}
                        value={field.value ?? ''}
                        updateValue={(_, value) => field.onChange(value)}
                        showFilter={false}
                        showFooter={false}
                        showChipBox={false}
                        multiple={false}
                        popoverMatchesFormControlWidth
                        renderValueDisplay="chips"
                        formControlVariant="outlined"
                        inputPlaceholderText="stringify-options"
                        disabled={isDisabled}
                        staticChoices={[
                          {
                            label: t('blox.editorial.status.draft.label'),
                            value: 'draft',
                          },
                          {
                            label: t('blox.editorial.status.published.label'),
                            value: 'published',
                          },
                          {
                            label: t('blox.editorial.status.unpublished.label'),
                            value: 'unpublished',
                          },
                        ]}
                      />
                    </span>
                  </Tooltip>
                </div>
              );
            }}
          />

          {/* Keywords */}
          <Controller
            name="keywords"
            control={control}
            render={({ field }) => (
              <div>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5, fontSize: '14px' }}
                >
                  {t('blox.editorial.asset.keywords.label')}
                </Typography>
                <SelectDropdownInput
                  inputLabel={t('blox.editorial.asset.keywords.label')}
                  value={Array.isArray(field.value) ? field.value : []}
                  updateValue={(_, value) => field.onChange(value)}
                  fetchDataFn={fetchAndFormatKeywords}
                  popoverMatchesFormControlWidth
                  renderValueDisplay="chips"
                  formControlVariant="outlined"
                  inputPlaceholderText="stringify-options"
                  filterPlaceholderText={t('blox.editorial.asset.keywords.help')}
                  noResultsMessage={t('blox.editorial.asset.keywords.not_found')}
                  multiple
                />
              </div>
            )}
          />

          {/* Slug */}
          <Controller
            name="slug"
            control={control}
            render={({ field }) => (
              <div data-testid="batch-import-slug-field">
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5, fontSize: '14px' }}
                >
                  {t('blox.editorial.metadata.group.print_slug.label')}
                </Typography>
                <TextDropdownInput
                  data-testid="slug-text-dropdown"
                  inputLabel={t('blox.editorial.metadata.group.print_slug.label')}
                  value={field.value ?? ''}
                  updateValue={(_, value) => field.onChange(value)}
                  applyChangesOnPopoverClose
                  popoverMatchesFormControlWidth
                  filterPlaceholderText={t(
                    'blox.editorial.metadata.group.print_slug.help',
                    'Find exact slug...',
                  )}
                  formControlVariant="outlined"
                  renderValueDisplay="chips"
                />
              </div>
            )}
          />

          {/* Authors */}
          <Controller
            name="authors"
            control={control}
            rules={{
              validate: (value) => {
                if (!value || (Array.isArray(value) && value.length === 0)) {
                  return 'At least one author is required';
                }
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <div>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 0.5, fontSize: '14px' }}
                  >
                    {t('blox.editorial.asset.authors.label_one')}
                  </Typography>
                  <SelectDropdownInput
                    inputLabel={t('blox.editorial.asset.authors.label_one')}
                    value={(field.value || []) as any}
                    updateValue={(_, value) => {
                      field.onChange(value);
                    }}
                    fetchDataFn={fetchAndFormatAuthors}
                    staticChoices={currentUserChoice ? [currentUserChoice] : []}
                    popoverMatchesFormControlWidth
                    formControlVariant="outlined"
                    inputPlaceholderText="stringify-options"
                    renderValueDisplay="chips"
                    isDataPrivate
                    multiple
                    showFilter={false}
                    showUnlabeledItems
                  />
                  {error && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {error.message}
                    </Typography>
                  )}
                </div>
              );
            }}
          />

          {/* Sections */}
          <Controller
            name="sections"
            control={control}
            render={({ field }) => (
              <div>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5, fontSize: '14px' }}
                >
                  {t('blox.editorial.asset.sections.label')}
                </Typography>
                <SelectDropdownInput
                  inputLabel={t('blox.editorial.asset.sections.label')}
                  value={Array.isArray(field.value) ? field.value : []}
                  updateValue={(_, value) => field.onChange(value)}
                  fetchDataFn={fetchAndFormatSections}
                  popoverMatchesFormControlWidth
                  renderValueDisplay="chips"
                  formControlVariant="outlined"
                  inputPlaceholderText="stringify-options"
                  multiple
                />
              </div>
            )}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

BatchImportUploadPresets.displayName = 'BatchImportUploadSettings';
